import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from '@store/state';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { AppRoutesConstant } from '@constants/app-routes.costant';
import { StepperBarStep } from '@shared/services/extras/_interfaces_/extras-steps.interface';
import { StepperBarStepsEnum } from '@enums/stepper-bar-steps.enum';
import {
  selectSelectedOrderDossierNumber,
  selectSelectedOrderFlightDetailsIdentifier,
} from '@store/selected-order/selected-order.selectors';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  PassengersResponse,
  Passenger,
  PassengerFormValues,
} from '@shared/services/passenger-change/_interfaces_/passenger-change.interface';
import { PaxChangeService } from '@shared/services/passenger-change/passenger-change.service';
import { PassengerAccordionComponent } from '../passenger-accordion/passenger-accordion.component';
import { PassengerChangeFormArrayName, PassengerChangeFormKeysEnum } from '@enums/passengerChangeFormKeys.enum';
import { ContentService } from '@shared/services/content/content.service';
import { ContentTypesEnum } from '@enums/content-type.enum';
import { ContentMapperService } from '@shared/services/content/content-mapper.service';
import { PassengerChangeContent } from '@shared/services/content/_interfaces_/content.interface';
import { AlertMessageContentMapped } from '@shared/services/content/_interfaces_/content.mapped.interface';
import { selectCommonOrderReference } from '@store/common-order/common-order.selectors';

@Component({
  selector: 'passenger-change-edit-component',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
})
export class PassengerChangeEditComponent implements OnDestroy, OnInit {
  @ViewChild(PassengerAccordionComponent) airportAssistanceComponent: PassengerAccordionComponent;
  private unsubscribe$ = new Subject();
  private dossierNumber: string;
  private orderReference: string;
  public flightIdentifier: string;
  public stepperBarSteps: Array<StepperBarStep>;
  public stepperBarStepsEnum = StepperBarStepsEnum;
  public isOverviewPageNavigationButtonDisabled: boolean = true;
  public paxData: Passenger[];
  public switchStateArray: boolean[] = [];
  public paxFormGroup: FormGroup;
  public paxFormArray: FormArray;
  public alertMessage$: Observable<AlertMessageContentMapped>;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private paxChangeService: PaxChangeService,
    private fb: FormBuilder,
    private contentService: ContentService,
    private contentMapperService: ContentMapperService
  ) {}

  ngOnInit() {
    this.paxFormGroup = this.fb.group({
      passengers: this.fb.array([]),
    });
    this.paxFormArray = this.paxFormGroup.get(PassengerChangeFormArrayName) as FormArray;

    this.getOrderReference();
    this.setStepperBarSteps();
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.setStepperBarSteps();
    });

    this.store
      .select(selectSelectedOrderFlightDetailsIdentifier)
      .pipe(
        take(1),
        switchMap((flightIdentifier) => {
          return this.paxChangeService.getPassengersData(flightIdentifier);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((response: PassengersResponse) => {
        this.flightIdentifier = response.flightIdentifier;
        this.paxData = response.passengers;
        this.switchStateArray = new Array(this.paxData.length).fill(false);
        response.passengers.forEach((passenger) => {
          this.paxFormArray.push(this.createPassengerGroup(passenger));
        });
      });

    this.retrieveAlertMessage();
  }

  private retrieveAlertMessage(): void {
    this.alertMessage$ = this.contentService
      .getContent<PassengerChangeContent>(ContentTypesEnum.passengerChange)
      .pipe(map((content: PassengerChangeContent) => this.contentMapperService.mapAlertMessage(content?.alertMessage)));
  }

  get passengerArray(): FormArray {
    return this.paxFormGroup.get(PassengerChangeFormArrayName) as FormArray;
  }

  createPassengerGroup(p: Passenger): FormGroup {
    return this.fb.group({
      [PassengerChangeFormKeysEnum.wasPassengerChanged]: false,
      [PassengerChangeFormKeysEnum.identifier]: p.identifier,
      [PassengerChangeFormKeysEnum.gender]: p.genderType,
      [PassengerChangeFormKeysEnum.firstName]: p.firstName,
      [PassengerChangeFormKeysEnum.lastName]: p.lastName,
      [PassengerChangeFormKeysEnum.dateOfBirth]: p.dateOfBirth,
    } as PassengerFormValues);
  }

  private getOrderReference(): void {
    this.store
      .select(selectCommonOrderReference)
      .pipe(take(1))
      .subscribe((orderReference: string) => {
        this.orderReference = orderReference;
      });
  }

  setStepperBarSteps(): void {
    this.stepperBarSteps = [
      { stepName: this.translateService.instant('STEPBAR_PASSENGER_CHANGE.STEP_1') },
      { stepName: this.translateService.instant('STEPBAR_PASSENGER_CHANGE.STEP_2') },
    ];
  }

  public switchEventChange($event: { state: boolean; index: number }): void {
    this.switchStateArray[$event.index] = $event.state;
  }

  public inputUpdateEventChange(wasUpdated: boolean): void {
    this.isSearchBtnDisabled(!wasUpdated);
  }

  public isSearchBtnDisabled(isDisabled: boolean): void {
    this.isOverviewPageNavigationButtonDisabled = isDisabled;
  }

  navigateToOrderOverViewPage(): void {
    if (this.orderReference) {
      this.router.navigateByUrl(`${UrlPathnameEnum.CommonOrder}/${this.orderReference}`);
    }
  }

  navigateToPassengerChangeOverviewPage(): void {
    if (!this.isOverviewPageNavigationButtonDisabled) {
      if (this.paxFormGroup.valid) {
        const formData = this.paxFormGroup.value.passengers?.filter((passenger) => passenger.wasPassengerChanged);
        this.paxChangeService
          .submitNewPassengersData(this.flightIdentifier, formData)
          .pipe(map((response) => response))
          .subscribe({
            next: (res) => {
              this.paxChangeService.setLastPassengerUpdateData(res);
              this.router.navigate([UrlPathnameEnum.PassengerChangeOverview], {
                state: { updateData: res },
              });
            },
            error: (err) => {},
          });
      } else {
        this.paxFormGroup.markAllAsTouched();
      }
    }
  }

  public formTrackBy(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }
}
